/**
 * TreatmentsTimelinePicker — pick which locked treatment reports feed the AI
 * summary. Presentational: the page owns the data + selection. Renders a
 * bounded, scrollable timeline of selectable rows: checkbox · timeline dot ·
 * date+time · type badge · short summary.
 *
 * Only locked, non-deleted reports should be passed in (soft-deleted / drafts
 * are never eligible for an exported summary).
 */

import { LockSimple, FileX } from "@phosphor-icons/react";
import { formatHebrewDate, formatTime } from "../../../utils/dateUtils";

export const MAX_SELECTABLE_TREATMENTS = 60; // mirrors server MAX_TREATMENTS

function TreatmentsTimelinePicker({ treatments, selectedIds, onChange, typeNameById }) {
  const toggle = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onChange(next);
  };
  const selectAll = () => onChange(new Set(treatments.map((t) => t.id)));
  const clearAll = () => onChange(new Set());

  if (treatments.length === 0) {
    return (
      <div className="ais-empty">
        <FileX size={20} />
        <span>אין דיווחי טיפול נעולים למטופל זה. ניתן לסכם רק דיווחים שננעלו.</span>
      </div>
    );
  }

  const selectedCount = treatments.filter((t) => selectedIds.has(t.id)).length;
  const overLimit = selectedCount > MAX_SELECTABLE_TREATMENTS;

  return (
    <div className="ais-picker">
      <div className="ais-picker-header">
        <span className="ais-picker-count">
          נבחרו <b>{selectedCount}</b> מתוך {treatments.length}
        </span>
        <div className="ais-picker-actions">
          <button
            type="button"
            className="pdx-link-btn"
            onClick={selectAll}
            disabled={selectedCount === treatments.length}
          >
            בחר הכל
          </button>
          <span className="ais-picker-sep" aria-hidden="true">
            ·
          </span>
          <button
            type="button"
            className="pdx-link-btn"
            onClick={clearAll}
            disabled={selectedCount === 0}
          >
            נקה בחירה
          </button>
        </div>
      </div>

      <div className="ais-timeline" role="group" aria-label="בחירת טיפולים לסיכום">
        {treatments.map((t, idx) => {
          const checked = selectedIds.has(t.id);
          const typeName = t.treatmentTypeId ? typeNameById.get(t.treatmentTypeId) : null;
          const preview = (t.shortSummary || "").trim();
          const isPlaceholder = !preview || preview.includes("מייצר תקציר");
          return (
            <label key={t.id} className={`ais-row ${checked ? "checked" : ""}`}>
              <input
                type="checkbox"
                className="ais-checkbox"
                checked={checked}
                onChange={() => toggle(t.id)}
                aria-label={`טיפול מתאריך ${formatHebrewDate(t.treatmentDate)}`}
              />
              <span className="ais-rail" aria-hidden="true">
                <span className="ais-dot" />
                {idx < treatments.length - 1 && <span className="ais-line" />}
              </span>
              <span className="ais-row-main">
                <span className="ais-row-top">
                  <span className="ais-date">
                    {formatHebrewDate(t.treatmentDate)}
                    <span className="ais-time"> · {formatTime(t.treatmentDate)}</span>
                  </span>
                  {typeName && <span className="ais-badge">{typeName}</span>}
                  <span className="ais-badge muted" title="דיווח נעול">
                    <LockSimple size={10} weight="fill" />
                  </span>
                </span>
                {t.title && <span className="ais-title">{t.title}</span>}
                <span className={`ais-preview ${isPlaceholder ? "empty" : ""}`}>
                  {isPlaceholder ? "ללא תקציר" : preview}
                </span>
              </span>
            </label>
          );
        })}
      </div>

      {overLimit && (
        <div className="prw-field-error">
          ניתן לסכם עד {MAX_SELECTABLE_TREATMENTS} טיפולים בבת אחת — הסירו חלק מהבחירה.
        </div>
      )}
    </div>
  );
}

export default TreatmentsTimelinePicker;
